/**
 * Hook for computing evidence source configuration.
 *
 * Centralizes all the logic for determining:
 * - Which domains to search
 * - Whether to include user docs
 * - Whether to include web search
 * - Source list IDs for web collections
 * - Integration partners configuration
 * - Evidence source filters
 */

import { selectedDomainsAtom, setSelectedDomainsAtom } from '@/atoms/chatAtoms';
import { evidenceSourceFiltersAtom, includeWebSearchAtom, sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import {
  getActiveCollectionIds,
  getIncludeUserDocs,
  normalizePersistedSourceControlState,
  partitionCollectionIds,
  resolveLibraryCollectionPruneIds,
} from '@/atoms/sourceControlSelectors';
import { DEFAULT_PARTNER_MAX_RESULTS } from '@/components/chat/constants/integrationPartners';
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import type { EvidenceSourceFilters } from '@heidi/evidence/contracts';
import {
  IS_EVIDENCE_LIBRARY_ENABLED,
  IS_EVIDENCE_USER_DOCS_ENABLED,
  IS_UNIFIED_COLLECTIONS_ENABLED,
} from '@/components/constants/flags';
import { useCollections } from '@heidi/evidence/data';
import { useSourceLists, useUnmigratedSourceLists } from '@heidi/evidence/data';
import { sanitizeSourceConfig } from '@/components/chat/utils/sanitizeSourceConfig';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useUserDocuments } from '@heidi/evidence/data';

const EMPTY_COLLECTION_IDS: string[] = [];

export interface EvidenceSourceConfig {
  /** Effective domains to search (null means all) */
  effectiveDomains: string[] | undefined;
  /** Whether to include user documents in search */
  includeUserDocs: boolean;
  /** Whether to include web search */
  includeWebSearch: boolean;
  /** Source list IDs for web collections */
  sourceListIds: string[] | undefined;
  /** User document collection IDs (legacy — documents only) */
  userDocCollectionIds: string[];
  /** Unified collection IDs for collection-backed sources. */
  collectionIds: string[];
  /** Integration partners configuration */
  integrationPartners:
    | Array<{ partner: string; enabled: boolean; max_results?: number; use_tavily?: boolean }>
    | undefined;
  /** Evidence source filters */
  evidenceSourceFilters: EvidenceSourceFilters;
}

/**
 * Hook that computes all evidence source configuration.
 * Extracts complex source/domain logic from useChat.
 */
export function useEvidenceSourceConfig(): EvidenceSourceConfig {
  const selectedDomains = useAtomValue(selectedDomainsAtom);
  const updateSelectedDomains = useSetAtom(setSelectedDomainsAtom);
  const sourceControlToggle = useAtomValue(sourceControlToggleAtom);
  const setSourceControlToggle = useSetAtom(sourceControlToggleAtom);

  // EVI-1368 / EVI-2407: prune against the visibility allowlist
  // (enforced ∪ optional), not the enforced-only list — optional IDs are
  // valid member selections and must survive rehydrate and write-back.
  const {
    isEnforced,
    sourceControl,
    enforcedLibraryCollectionIds,
    optionalLibraryCollectionIds,
    libraryCollectionAllowlistIds,
    libraryCollectionIdsChangeable,
    libraryCollectionIdListsActive,
    enforcedWebCollectionIds,
    webCollectionIdsChangeable,
    isPerCollectionLibraryModesEnabled,
  } = useTeamEvidenceEnforcement();

  const { data: allowedDomains } = useQueryUser(
    (data) => data?.session_settings?.allowed_domains
  );

  // User documents for evidence - only fetch when feature is enabled AND user is authenticated
  const { isAuthenticated } = useIsAuthenticated();
  const isUserDocsFeatureEnabled = IS_EVIDENCE_USER_DOCS_ENABLED();
  const { data: userDocsData } = useUserDocuments({
    enabled: isUserDocsFeatureEnabled && isAuthenticated,
  });

  // Include user documents in search when feature flag is enabled and user has uploaded docs.
  const hasUserDocs = (userDocsData?.documents?.length ?? 0) > 0;
  const isUnifiedEnabled = IS_UNIFIED_COLLECTIONS_ENABLED();
  const { data: collectionsData, isLoading: isCollectionsLoading } = useCollections();
  const { data: sourceLists } = useSourceLists();
  const {
    data: unmigratedSourceLists,
    isLoading: isUnmigratedSourceListsLoading,
  } = useUnmigratedSourceLists();

  const normalizedSourceControlToggle = useMemo(() => {
    if (isUnifiedEnabled) {
      if (
        isCollectionsLoading ||
        isUnmigratedSourceListsLoading ||
        collectionsData === undefined ||
        unmigratedSourceLists === undefined
      ) {
        return sourceControlToggle;
      }

      const validUnifiedIds = [
        ...collectionsData.collections.map((collection) => collection.collection_id),
        ...unmigratedSourceLists.map((sourceList) => sourceList.id),
      ];

      return normalizePersistedSourceControlState(sourceControlToggle, {
        validLibraryCollectionIds: validUnifiedIds,
        validWebCollectionIds: validUnifiedIds,
        enforcedLibraryCollectionIds: resolveLibraryCollectionPruneIds(
          libraryCollectionAllowlistIds,
          enforcedLibraryCollectionIds
        ),
        enforcedWebCollectionIds,
      });
    }

    return normalizePersistedSourceControlState(sourceControlToggle, {
      validLibraryCollectionIds: collectionsData?.collections.map(
        (collection) => collection.collection_id
      ),
      validWebCollectionIds: sourceLists?.map((list) => list.id),
      enforcedLibraryCollectionIds: resolveLibraryCollectionPruneIds(
        libraryCollectionAllowlistIds,
        enforcedLibraryCollectionIds
      ),
      enforcedWebCollectionIds,
    });
  }, [
    isUnifiedEnabled,
    isCollectionsLoading,
    isUnmigratedSourceListsLoading,
    collectionsData,
    unmigratedSourceLists,
    sourceControlToggle,
    sourceLists,
    libraryCollectionAllowlistIds,
    enforcedLibraryCollectionIds,
    enforcedWebCollectionIds,
  ]);

  const includeUserDocs = getIncludeUserDocs(
    normalizedSourceControlToggle,
    isUnifiedEnabled,
    !!isUserDocsFeatureEnabled,
    hasUserDocs
  );

  // Disable web search when ONLY library mode is enabled (no web sources at all)
  const includeWebSearch = useAtomValue(includeWebSearchAtom);
  const isWebCollectionsEnabled = IS_EVIDENCE_LIBRARY_ENABLED();

  // Compute source list IDs to pass to backend.
  // Unified mode: source_list_ids is not used (UUIDs can't go in this field).
  const sourceListIds = useMemo(() => {
    if (!isWebCollectionsEnabled || isUnifiedEnabled) return undefined;
    if (!normalizedSourceControlToggle.webCollections || !sourceLists) return undefined;

    if (normalizedSourceControlToggle.selectedWebCollectionIds.length === 0) {
      const allIds = sourceLists.map((list) => list.id);
      return allIds.length > 0 ? allIds : undefined;
    }

    const validIds = normalizedSourceControlToggle.selectedWebCollectionIds.filter((id) =>
      sourceLists.some((list) => list.id === id)
    );
    return validIds.length > 0 ? validIds : undefined;
  }, [
    isWebCollectionsEnabled,
    isUnifiedEnabled,
    normalizedSourceControlToggle.webCollections,
    normalizedSourceControlToggle.selectedWebCollectionIds,
    sourceLists,
  ]);

  // EVI-1371: sync the pruned memo back to the persisted atom so direct readers (e.g. `store.get(sourceControlToggleAtom)` in `useChatStream`) see the new allowlist mid-session, not just on next user interaction.
  useEffect(() => {
    if (normalizedSourceControlToggle === sourceControlToggle) return;
    setSourceControlToggle(normalizedSourceControlToggle);
  }, [normalizedSourceControlToggle, sourceControlToggle, setSourceControlToggle]);

  // EVI-1098: Compute integration partners to pass to backend
  const { partners: availablePartners } = useAvailableIntegrations();

  const integrationPartners = useMemo(() => {
    if (availablePartners.length === 0) return undefined;
    const enabledPartners = availablePartners
      .filter(
        (partner) => normalizedSourceControlToggle.integrationPartners[partner.id] === true
      )
      .map((partner) => ({
        partner: partner.id,
        enabled: true,
        max_results: DEFAULT_PARTNER_MAX_RESULTS,
        ...(partner.useTavily !== undefined && { use_tavily: partner.useTavily }),
      }));
    return enabledPartners.length > 0 ? enabledPartners : undefined;
  }, [availablePartners, normalizedSourceControlToggle.integrationPartners]);

  // Initialize selectedDomainsAtom from allowedDomains when it becomes available
  useEffect(() => {
    if (allowedDomains && allowedDomains.length > 0) {
      if (selectedDomains.size === 0) {
        updateSelectedDomains(allowedDomains);
      }
    }
  }, [allowedDomains, selectedDomains.size, updateSelectedDomains]);

  // EVI-405: backend resolves domains from evidenceSourceFilters when badges UI is on.
  const effectiveDomains = undefined;

  // EVI-493: Evidence source filters from toggle state.
  const evidenceSourceFilters = useAtomValue(evidenceSourceFiltersAtom);

  // Collection IDs via shared selector (handles unified vs legacy bucket merging).
  const activeIds = useMemo(
    () => getActiveCollectionIds(normalizedSourceControlToggle, isUnifiedEnabled),
    [normalizedSourceControlToggle, isUnifiedEnabled]
  );

  // Unified mode: partition IDs — UUIDs → collection_ids, legacy hex → source_list_ids.
  // Legacy mode: all IDs go to user_doc_collection_ids.
  const partitioned = isUnifiedEnabled ? partitionCollectionIds(activeIds) : null;
  const collectionIds = partitioned?.collectionIds ?? EMPTY_COLLECTION_IDS;
  const userDocCollectionIds = useMemo(
    () => (isUnifiedEnabled ? [] : activeIds),
    [isUnifiedEnabled, activeIds]
  );
  // Legacy source list hex IDs from allCollections (unmigrated) must go via source_list_ids.
  const mergedSourceListIds = sourceListIds ?? (
    partitioned && partitioned.legacySourceListIds.length > 0
      ? partitioned.legacySourceListIds
      : undefined
  );

  // Team evidence enforcement — legacy fields plus unified collection_ids (EVI-2179).
  const sanitized = useMemo(
    () =>
      sanitizeSourceConfig(
        {
          evidenceSourceFilters,
          userDocCollectionIds,
          sourceListIds: mergedSourceListIds,
          integrationPartners,
          includeUserDocs,
          collectionIds: isUnifiedEnabled ? collectionIds : undefined,
        },
        {
          isEnforced,
          sourceControl,
          enforcedLibraryCollectionIds,
          optionalLibraryCollectionIds,
          libraryCollectionIdsChangeable,
          libraryCollectionIdListsActive,
          enforcedWebCollectionIds,
          webCollectionIdsChangeable,
          isPerCollectionLibraryModesEnabled,
        }
      ),
    [
      isEnforced,
      sourceControl,
      evidenceSourceFilters,
      userDocCollectionIds,
      mergedSourceListIds,
      integrationPartners,
      includeUserDocs,
      collectionIds,
      isUnifiedEnabled,
      enforcedLibraryCollectionIds,
      optionalLibraryCollectionIds,
      libraryCollectionIdsChangeable,
      libraryCollectionIdListsActive,
      enforcedWebCollectionIds,
      webCollectionIdsChangeable,
      isPerCollectionLibraryModesEnabled,
    ]
  );

  return {
    effectiveDomains,
    includeUserDocs: sanitized.includeUserDocs,
    includeWebSearch,
    sourceListIds: sanitized.sourceListIds,
    userDocCollectionIds: sanitized.userDocCollectionIds,
    collectionIds: sanitized.collectionIds ?? collectionIds,
    integrationPartners: sanitized.integrationPartners,
    evidenceSourceFilters: sanitized.evidenceSourceFilters,
  };
}
